import { ComponentProps } from 'react';

import { useLocale, useTranslations } from 'next-intl';
import { Maybe } from 'yup';

import StringHelper from '@/app/[lang]/(search)/_components/shared/helper/string.helper';
import MarketPriceBadge, {
    MINIMUM_PRICE_SHOW_MARKET_BADGE,
} from '@/app/_components/MarketPriceBadge/MarketPriceBadge';
import {
    getPriceDrop,
    getSavePriceDrop,
    getSavePriceDropCompact,
} from '@/app/_helper/property';
import { cn } from '@/app/_helper/tailwind';
import {
    FormattedValue,
    Location,
    MarketValuation,
    PreferredCurrency,
    Property,
} from '@/modules/core/gql/generated';
import { PropertyTypeEnum } from '@/modules/property/property.type';

import KPRSimulation from './KPRSimulation';

type PriceInfoProps = {
    size?: 'default' | 'tiny' | 'small' | 'medium' | 'big';
    price: string;
    totalDisplay?: string;
    installment?: Property['installment'];
    priceDrop: Property['priceDrop'];
    expandPriceDropText?: boolean;
    isSimpleView?: boolean;
    location?: Location;
    offer?: Property['price']['offer'];
    propertyType?: FormattedValue;
    searchPath?: string[];
    showCalculator?: boolean;
    priceTypeValue?: string;
    isBestDeal?: boolean;
    priceClassName?: string;
    preferredCurrency?: PreferredCurrency | undefined;
    marketValuation?: Maybe<MarketValuation>;
    isNpl?: boolean;
    isLand?: boolean;
    enableCompactSavingLabel?: boolean;
} & ComponentProps<'button'>;
const PriceInfo = ({
    size = 'default',
    price,
    totalDisplay,
    installment,
    priceDrop,
    className,
    priceClassName,
    location,
    propertyType,
    searchPath,
    offer,
    isSimpleView = false,
    showCalculator,
    priceTypeValue,
    isBestDeal,
    preferredCurrency,
    marketValuation,
    isNpl,
    isLand,
    enableCompactSavingLabel,
}: PriceInfoProps) => {
    const locale = useLocale();
    const t = useTranslations('common');

    const calculatedOldPrice =
        (priceDrop?.oldPrice || 0) / (preferredCurrency?.value || 1);

    const calculatedAmountPrice =
        (priceDrop?.amountPrice || 0) / (preferredCurrency?.value || 1);

    const baseCurrency =
        preferredCurrency?.baseCurrency || (locale === 'en' ? 'IDR' : 'Rp');

    const oldPrice = getPriceDrop(calculatedOldPrice, t, locale)
        ?.replace('IDR', preferredCurrency?.baseCurrency || 'IDR')
        ?.replace('Rp', preferredCurrency?.baseCurrency || 'Rp');

    const minimumMarketPrice =
        MINIMUM_PRICE_SHOW_MARKET_BADGE / (preferredCurrency?.value || 1);

    const showMarketPrice =
        isNpl &&
        (marketValuation?.district?.savingsAgainstMarket?.offer ||
            marketValuation?.city?.savingsAgainstMarket?.offer ||
            0) >= minimumMarketPrice;

    const savingsFromMarketPrice =
        (marketValuation?.district?.savingsAgainstMarket?.offer ||
            marketValuation?.city?.savingsAgainstMarket?.offer ||
            0) / (preferredCurrency?.value || 1);

    return (
        <div>
            <div
                className={cn(
                    'flex items-baseline gap-1 mt-1 flex-col',
                    {
                        'flex-wrap': Boolean(priceDrop),
                        'flex-nowrap w-full overflow-hidden': !priceDrop,
                        'flex-col':
                            size === 'small' ||
                            size === 'medium' ||
                            Boolean(installment?.formattedValue),
                    },
                    className,
                )}
            >
                <div
                    className={cn(
                        'flex items-baseline gap-x-1',
                        {
                            'flex-wrap': Boolean(priceDrop),
                            'flex-nowrap w-full overflow-hidden': !priceDrop,
                            'flex-col':
                                !isNpl &&
                                (size === 'small' ||
                                    size === 'medium' ||
                                    size === 'tiny'),
                            'gap-x-2': isNpl,
                        },
                        className,
                    )}
                    data-name='price-info'
                >
                    <span
                        className={cn(
                            {
                                'text-primary font-bold whitespace-pre': true,
                                'text-lg leading-[18px]': size === 'default',
                                'text-sm leading-4': size === 'tiny',
                                'text-base leading-6': size === 'small',
                                'text-lg': size === 'medium',
                                'md:text-2xl md:leading-6 text-lg leading-[16px]':
                                    size === 'big',
                            },
                            priceClassName,
                        )}
                        data-testid='ldp-text-price'
                    >
                        {isLand && totalDisplay ? totalDisplay : price}
                    </span>
                    {!showMarketPrice && isBestDeal && (
                        <div className='gap-2 self-stretch px-1 my-auto h-4 text-[10px] font-semibold leading-relaxed text-white bg-emerald-400 rounded-[100px] flex items-center'>
                            Best Deal!
                        </div>
                    )}
                    {!showMarketPrice &&
                        priceDrop &&
                        priceTypeValue !== '1' && (
                            <span className='flex gap-x-1 items-center'>
                                <span className='text-greyText font-medium leading-4 text-xs line-through'>
                                    {priceDrop.oldPrice ? oldPrice : ''}
                                </span>
                                <span
                                    data-test-id="badge-hemat"
                                    data-name='market-price'
                                    className='text-accent mr-1 font-medium text-3xs p-1 rounded-[4px] border border-solid border-secondaryHover inline-flex items-center gap-x-1'
                                >
                                    <svg
                                        width='12'
                                        height='12'
                                        viewBox='0 0 12 12'
                                        fill='none'
                                        xmlns='http://www.w3.org/2000/svg'
                                    >
                                        <path
                                            fillRule='evenodd'
                                            clipRule='evenodd'
                                            d='M1.70711 6.70711C1.31658 6.31658 1.31658 5.68342 1.70711 5.29289L5.70711 1.29289C5.89464 1.10536 6.149 1 6.41421 1H10C10.5523 1 11 1.44772 11 2V5.58579C11 5.851 10.8946 6.10536 10.7071 6.29289L6.70711 10.2929C6.31658 10.6834 5.68342 10.6834 5.29289 10.2929L1.70711 6.70711ZM9.75 3.5C10.0952 3.15485 10.0952 2.59515 9.75 2.25C9.40485 1.90485 8.84515 1.90485 8.5 2.25C8.15485 2.59515 8.15485 3.15485 8.5 3.5C8.84515 3.84515 9.40485 3.84515 9.75 3.5Z'
                                            fill='#D70C24'
                                        />
                                    </svg>

                                    {locale === 'en' ? 'SAVE ± ' : 'HEMAT '}
                                    {enableCompactSavingLabel
                                        ? `${baseCurrency} ${getSavePriceDropCompact(
                                              calculatedAmountPrice || 0,
                                              t,
                                              locale,
                                          )}`
                                        : `${baseCurrency} ${getSavePriceDrop(
                                              calculatedAmountPrice || 0,
                                              t,
                                              locale,
                                          )}`}
                                </span>
                            </span>
                        )}

                    {showMarketPrice &&
                        savingsFromMarketPrice >= minimumMarketPrice && (
                            <MarketPriceBadge
                                url='#'
                                size='small'
                                text={`${
                                    locale === 'en'
                                        ? 'Save up to'
                                        : 'Lebih Murah'
                                } ${StringHelper.getFormattedNumber(
                                    savingsFromMarketPrice,
                                    0,
                                    locale as 'id' | 'en',
                                    true,
                                )}`}
                            />
                        )}
                </div>
                {!!totalDisplay && isLand && (
                    <span className='text-[#6e7686] text-sm font-normal pt-1'>
                        {price}
                    </span>
                )}
            </div>
            {showCalculator &&
                ((+(propertyType?.value || '16') !==
                    PropertyTypeEnum.newLaunch &&
                    !searchPath) ||
                    !isSimpleView) && (
                    <KPRSimulation
                        price={offer || 0}
                        location={
                            location || ({ city: { id: '' } } as Location)
                        }
                        title={installment?.formattedValue
                            ?.split('/')
                            ?.join(' per ')}
                    />
                )}
        </div>
    );
};

export default PriceInfo;